import type { EyeOpenness } from '../types';

/**
 * Eye-openness gate. Replaces the old luminance-based pixel heuristics
 * (contrast / pupil-dark-fraction / lip-seam), which normalized against a
 * "smooth skin" reference and so false-rejected faces whose reference landed
 * on facial hair (full beards) or whose darks compressed under low light /
 * dark skin. These signals are geometric + ML instead:
 *
 * - EAR (Eye Aspect Ratio): eyelid-landmark geometry. A ratio of eye HEIGHT to
 *   WIDTH, so it is scale- and brightness-invariant — an open eye reads ~0.25+,
 *   a closed/blinking eye collapses toward ~0.1. Pigmentation-independent.
 * - eyeBlink blendshape: the Face Landmarker's ML eye-closure score (0–1),
 *   more normalized across head pose and eye shape than raw EAR.
 *
 * Eyes count as open only when BOTH agree (wide geometry AND low blink), so a
 * mid-blink frame can't auto-capture. True OCCLUSION (hand/phone/mask) is no
 * longer judged here — Rekognition's server-side FaceOccluded is the
 * authoritative backstop (per the ESS integration spec).
 */

/**
 * FaceMesh landmark indices for the 6-point EAR, per eye, in the order
 * [outer, top1, top2, inner, bottom2, bottom1]:
 *   EAR = (|top1−bottom1| + |top2−bottom2|) / (2·|outer−inner|)
 */
export const RIGHT_EYE_EAR = [33, 160, 158, 133, 153, 144] as const;
export const LEFT_EYE_EAR = [362, 385, 387, 263, 373, 380] as const;

/** Enter-open EAR: a stably-framed face must reach at least this to be "open". */
export const MIN_EYE_ASPECT_RATIO = 0.2;
/** Stay-open EAR (hysteresis): once open, it holds until EAR drops below this. */
export const EXIT_EYE_ASPECT_RATIO = 0.17;
/** Enter-open blink ceiling: the eyeBlink score must be at or below this. */
export const MAX_EYE_BLINK = 0.5;
/** Stay-open blink ceiling (hysteresis): once open, holds until blink exceeds this. */
export const EXIT_EYE_BLINK = 0.6;

export interface EyesOpenResult {
  eyesOpen: boolean;
  /** min(earLeft, earRight) — the more-closed eye drives the verdict. */
  earMin: number;
  /** max(blinkLeft, blinkRight) — the more-blinking eye drives the verdict. */
  blinkMax: number;
}

/** Euclidean distance between two points. */
function dist(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * Eye Aspect Ratio from six eyelid points (in the RIGHT_EYE_EAR / LEFT_EYE_EAR
 * order), in pixel coordinates. Returns 0 for a degenerate (zero-width) eye.
 */
export function computeEar(points: { x: number; y: number }[]): number {
  const width = dist(points[0]!, points[3]!);
  if (width === 0) return 0;
  return (dist(points[1]!, points[5]!) + dist(points[2]!, points[4]!)) / (2 * width);
}

/**
 * Pure eyes-open decision over the two signals, with hysteresis. `wasOpen` is
 * the previous verdict: once open, the looser exit thresholds keep it open
 * through borderline frames (no tick-to-tick flicker); once closed, the
 * stricter enter thresholds must be cleared to re-open. Eyes are open only
 * when the geometry AND the blink score both say so.
 */
export function decideEyesOpen(signals: EyeOpenness, wasOpen = false): EyesOpenResult {
  const earMin = Math.min(signals.earLeft, signals.earRight);
  const blinkMax = Math.max(signals.blinkLeft, signals.blinkRight);
  const earThreshold = wasOpen ? EXIT_EYE_ASPECT_RATIO : MIN_EYE_ASPECT_RATIO;
  const blinkThreshold = wasOpen ? EXIT_EYE_BLINK : MAX_EYE_BLINK;
  return {
    eyesOpen: earMin >= earThreshold && blinkMax <= blinkThreshold,
    earMin,
    blinkMax,
  };
}
